// Analytics (PostHog) + error monitoring (Sentry), centralized.
//
// Both SDKs HARD NO-OP when their env keys are absent, AND are now DYNAMICALLY IMPORTED only when
// a key is present — so they are kept out of the initial JS bundle entirely when unset, and loaded
// off the critical render path (as separate chunks) when set. The module refs `_sentry`/`_posthog`
// are populated once the dynamic import resolves; every public fn guards on its enabled flag, which
// only flips true AFTER the import + init succeed, so any call before then is a safe no-op.
//
// Privacy: only categorical props (tool name, plan, booleans) — never generation content,
// full addresses, tokens, or secrets. Demo users (ids prefixed "demo-") are never identified.

import { SENTRY_DSN, POSTHOG_KEY, POSTHOG_HOST } from "../config/env";

let _sentry = null;
let _posthog = null;
let _phEnabled = false;
let _sentryEnabled = false;

// Drop anything that could carry a token / request body from a Sentry event (defense in depth
// on top of sendDefaultPii:false).
function _beforeSend(event) {
  try {
    if (event.request) {
      delete event.request.cookies;
      delete event.request.data;
      if (event.request.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
      }
    }
  } catch {}
  return event;
}

export async function initAnalytics() {
  if (SENTRY_DSN && !_sentryEnabled) {
    try {
      _sentry = await import("@sentry/react");
      _sentry.init({
        dsn: SENTRY_DSN,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        sendDefaultPii: false,
        beforeSend: _beforeSend,
      });
      _sentryEnabled = true;
    } catch { _sentry = null; /* stay silent — monitoring must never break the app */ }
  }
  if (POSTHOG_KEY && !_phEnabled) {
    try {
      _posthog = (await import("posthog-js")).default;
      _posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        autocapture: true,
        persistence: "localStorage+cookie",
      });
      _phEnabled = true;
    } catch { _posthog = null; /* stay silent */ }
  }
}

// Fire a product event. props must be categorical (no user content / PII / secrets).
export function track(event, props) {
  if (!_phEnabled || !_posthog) return;
  try { _posthog.capture(event, props || {}); } catch {}
}

// Tie events to a real user. Skips demo users so they don't pollute conversion/retention data.
export function identify(userId, props) {
  if (!_phEnabled || !_posthog || !userId) return;
  if (String(userId).startsWith("demo-")) return;
  try {
    _posthog.identify(String(userId), props || {});
    if (_sentryEnabled && _sentry) _sentry.setUser({ id: String(userId) });
  } catch {}
}

// On logout — clear the identified person so the next user isn't merged into this one.
export function resetAnalytics() {
  if (_phEnabled && _posthog) { try { _posthog.reset(); } catch {} }
  if (_sentryEnabled && _sentry) { try { _sentry.setUser(null); } catch {} }
}

// Report a caught error to Sentry (used by the app's ErrorBoundary). No-op without a DSN.
export function captureError(err, context) {
  if (!_sentryEnabled || !_sentry) return;
  try { _sentry.captureException(err, context ? { extra: context } : undefined); } catch {}
}
